import { useEffect, useLayoutEffect } from "react";

// SSR-safe useLayoutEffect fallback
const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;

/**
 * Parses color string into [r, g, b, a] in 0-255 range (alpha in 0-1).
 * Uses canvas 2d context for universal color parsing (handles rgb, rgba, hex, hsl, oklch, color(), etc.).
 */
function parseColor(colorStr: string): [number, number, number, number] | null {
  if (!colorStr || colorStr === "transparent" || colorStr === "inherit" || colorStr === "initial") {
    return null;
  }

  // Fast path for rgb/rgba
  const rgbMatch = colorStr.match(/rgba?\s*\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)(?:[,\s/]+([\d.]+))?\s*\)/i);
  if (rgbMatch) {
    const r = parseFloat(rgbMatch[1]);
    const g = parseFloat(rgbMatch[2]);
    const b = parseFloat(rgbMatch[3]);
    const a = rgbMatch[4] !== undefined ? parseFloat(rgbMatch[4]) : 1;
    return [r, g, b, a];
  }

  if (typeof document === "undefined") {
    return null;
  }

  try {
    const canvas = document.createElement("canvas");
    canvas.width = 1;
    canvas.height = 1;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return null;
    ctx.clearRect(0, 0, 1, 1);
    ctx.fillStyle = colorStr;
    ctx.fillRect(0, 0, 1, 1);
    const data = ctx.getImageData(0, 0, 1, 1).data;
    return [data[0], data[1], data[2], data[3] / 255];
  } catch {
    return null;
  }
}

/**
 * Calculates relative luminance according to WCAG 2.1 specs.
 */
function getRelativeLuminance(r: number, g: number, b: number): number {
  const [rs, gs, bs] = [r / 255, g / 255, b / 255].map((c) => {
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/**
 * Computes WCAG contrast ratio between two luminance values.
 */
function getContrastRatio(lum1: number, lum2: number): number {
  const lighter = Math.max(lum1, lum2);
  const darker = Math.min(lum1, lum2);
  return (lighter + 0.05) / (darker + 0.05);
}

const TEXT_BEARING_TAGS = new Set([
  "P",
  "LI",
  "TD",
  "TH",
  "SPAN",
  "STRONG",
  "B",
  "EM",
  "I",
  "A",
  "H1",
  "H2",
  "H3",
  "H4",
  "H5",
  "H6",
  "BLOCKQUOTE",
  "FIGCAPTION",
  "SMALL",
  "MARK",
  "CODE",
  "LABEL",
  "DIV",
  "FONT",
]);

/**
 * Hook to adjust text color of article elements when contrast with effective background is insufficient (< 4.5).
 * SSR-safe, mutation-observer guarded against re-triggering.
 */
export function useReadableArticleText(
  ref: React.RefObject<HTMLElement | null>,
  deps: unknown[]
): void {
  useIsomorphicLayoutEffect(() => {
    if (typeof window === "undefined") return;

    const root = ref.current;
    if (!root) return;

    let isApplying = false;

    const checkAndAdjustContrast = () => {
      if (!ref.current) return;
      const container = ref.current;

      const elements = container.querySelectorAll("*");
      const candidates: HTMLElement[] = [];

      // Check root container itself if it directly bears text
      const allToCheck: HTMLElement[] = [container, ...Array.from(elements) as HTMLElement[]];

      for (const el of allToCheck) {
        if (!TEXT_BEARING_TAGS.has(el.tagName)) {
          // Check if it directly contains non-empty text node
          let hasDirectText = false;
          for (let i = 0; i < el.childNodes.length; i++) {
            const child = el.childNodes[i];
            if (child.nodeType === Node.TEXT_NODE && child.textContent?.trim()) {
              hasDirectText = true;
              break;
            }
          }
          if (!hasDirectText) continue;
        } else {
          // Has text bearing tag; verify it actually has non-whitespace text
          if (!el.textContent?.trim()) continue;
        }
        candidates.push(el);
      }

      for (const el of candidates) {
        // Step 1: Check if element or any ancestor has background-image (not 'none')
        let hasBgImage = false;
        let curr: HTMLElement | null = el;
        while (curr) {
          const style = window.getComputedStyle(curr);
          if (style.backgroundImage && style.backgroundImage !== "none") {
            hasBgImage = true;
            break;
          }
          if (curr === container.parentElement) break; // stop past parent article/card
          curr = curr.parentElement;
        }
        if (hasBgImage) {
          continue;
        }

        // Step 2: Compute effective background by walking up ancestors to first non-transparent color
        // Default white [255, 255, 255, 1] if none
        let bgRgba: [number, number, number, number] = [255, 255, 255, 1];
        curr = el;
        while (curr) {
          const style = window.getComputedStyle(curr);
          const parsed = parseColor(style.backgroundColor);
          if (parsed && parsed[3] > 0.05) {
            bgRgba = parsed;
            break;
          }
          curr = curr.parentElement;
        }

        const bgLum = getRelativeLuminance(bgRgba[0], bgRgba[1], bgRgba[2]);

        // Step 3: Read computed text color
        const computedColor = window.getComputedStyle(el).color;
        const textRgba = parseColor(computedColor);
        if (!textRgba) continue;

        const textLum = getRelativeLuminance(textRgba[0], textRgba[1], textRgba[2]);
        const contrast = getContrastRatio(bgLum, textLum);

        // Step 4: If contrast < 4.5, adjust color
        if (contrast < 4.5) {
          if (bgLum > 0.5) {
            el.style.setProperty("color", "#1F2937", "important");
          } else {
            el.style.setProperty("color", "#F9FAFB", "important");
          }
        }
      }
    };

    // Initial check
    checkAndAdjustContrast();

    // Setup MutationObserver
    const observer = new MutationObserver(() => {
      if (isApplying) return;
      isApplying = true;
      observer.disconnect();
      try {
        checkAndAdjustContrast();
      } finally {
        if (ref.current) {
          observer.observe(ref.current, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ["style", "class"],
          });
        }
        isApplying = false;
      }
    });

    observer.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["style", "class"],
    });

    return () => {
      observer.disconnect();
    };
  }, deps);
}
